/**
 * Trail Bundle
 * Bundle único: tracking de interacciones, TrailScore → Meta, atribución UTM y conversión GA4.
 * Documentación: docs/trail-bundle/README.md
 *
 * Instalación (Shopify u otro sitio):
 * 1. Sube trail-bundle.js a tu tema o carpeta de assets
 * 2. Carga gtag/GA4 antes del bundle si usas eventos GA4
 * 3. Agrega antes de </head> (config antes o después del script):
 *
 *    Opción A - Config antes del script:
 *    <script>
 *      window.TrailConfig = {
 *        debug: false,
 *        events: {
 *          scrolling: false
 *        },
 *        analytics: {
 *          googleAnalytics: {
 *            enabled: true,
 *            sendTo: 'G-TRAIL' // opcional: todos los eventos Trail solo a esta propiedad
 *          }
 *        },
 *        trailScoreMeta: {
 *          enabled: true,
 *          debug: true,
 *          version: 'trailscore_v1',
 *          weights: { }, // pegar objeto weights JSON desde ghost-guru
 *          tiers: {
 *            core:   { eventName: 'trail_high_intent_core',   threshold: 8.5 },
 *            medium: { eventName: 'trail_high_intent_medium', threshold: 7.0 },
 *            max:    { eventName: 'trail_high_intent_max',    threshold: 5.5 }
 *          },
 *          activeTier: 'medium',
 *          attribution: {
 *            enabled: true,
 *            storageKey: 'trail_session_data',
 *            maxAgeDays: 7,
 *            exposeApi: true
 *          },
 *          conversion: {
 *            enabled: true,
 *            eventName: 'trail_conversion',
 *            pathPrefixes: ['/pages/gracias'],
 *            oncePerSession: true,
 *            conversionType: 'subscription',
 *            ga4: { enabled: true }
 *          }
 *        }
 *      };
 *    </script>
 *    <script src="{{ 'trail-bundle.js' | asset_url }}" defer></script>
 *
 *    HTML genérico:
 *    <script src="/assets/js/trail-bundle.js" defer></script>
 *
 *    Opción B - Config después del script (también funciona):
 *    <script src="{{ 'trail-bundle.js' | asset_url }}" defer></script>
 *    <script>
 *      window.TrailConfig = {
 *        debug: false,
 *        analytics: {
 *          googleAnalytics: { enabled: true }
 *        }
 *      };
 *    </script>
 */

(function(window, document) {
    'use strict';

    // ============================================
    // CONFIGURACIÓN
    // ============================================
    const defaultConfig = {
        enabled: true,
        debug: false, // Por defecto muestra logs (modo silencioso para analytics)
        sessionTimeout: 24 * 60 * 60 * 1000, // 24 horas de inactividad
        
        // Eventos a trackear
        events: {
            pageLoad: true,
            clicks: true,
            formInteractions: true,
            formSubmissions: true,
            scrolling: false,
            timeOnPage: true,
            pageNavigation: true,
            videoInteractions: true
        },
        
        // Scroll tracking
        scroll: {
            debounceMs: 500,
            trackPercentages: [25, 50, 75, 90, 100]
        },
        
        // Time milestones (segundos)
        timeMilestones: [15, 35, 90, 150, 180],
        
        // Analytics - DESHABILITADO POR DEFECTO (modo silencioso)
        analytics: {
            googleAnalytics: {
                enabled: false, // No envía a GA4 por defecto
                sendTo: null // null = GA4 del sitio; 'G-XXXX' = solo propiedad Trail
            },
            customEndpoint: {
                enabled: false, // No envía a endpoint por defecto
                url: null,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        },
        
        // Campos sensibles (no se loguean valores)
        sensitiveFields: [
            'password', 'pass', 'pwd', 'credit', 'card', 'cvv', 'cvc',
            'ssn', 'social', 'pin', 'secret', 'token', 'key'
        ],
        
        // Selectores a ignorar
        ignoreSelectors: [
            '.interaction-tracker-ignore',
            '.trail-ignore',
            '.adpulse-ignore'
        ],
        
        // Retención de datos
        dataRetention: {
            maxInteractionsInMemory: 1000
        }
    };

    // Merge con configuración personalizada
    // NO leer aquí, se leerá cuando se inicialice el tracker
    const getConfig = function() {
        return Object.assign({}, defaultConfig, window.TrailConfig || {});
    };

    // ============================================
    // TRAILSCORE → META (sidecar dentro del bundle)
    // ============================================
    const defaultTrailScoreMetaConfig = {
        enabled: true,
        debug: true,
        version: 'trailscore_v1',
        dayMode: 'utc', // 'utc' | 'local'
        eventName: 'trail_high_intent',
        threshold: 8.5,
        weights: {
            "trail_form_change_molienda_count": 3,
            "trail_form_change_tamano_count": 3,
            "trail_form_change_cantidad_count": 2,
            "trail_click_on_suscripciones_collection_count": 2,
            "trail_click_on_collections_cafe_count": 1.5,
            "trail_click_on_collections_kiosco_count": 1,
            "trail_click_on_collections_merch_count": 1,
            "trail_click_on_plan_mundial_count": 2,
            "trail_click_on_plan_brasil_count": 0.8,
            "trail_click_on_brasil_crucera_count": 3,
            "visited_suscripciones": 1,
            "visited_plan_pdp": 0.8
        },
        excludePathPrefixes: ['/account'],
        sessionTtlMs: 30 * 60 * 1000,
        storage: {
            userIdKey: 'trail_user_id',
            lastSentDayKey: 'trail_high_intent_last_sent_day',
            sessionPrefix: 'trail_session_signals_',
            sentInSessionPrefix: 'trail_high_intent_sent_',
            lockPrefix: 'trail_high_intent_lock_'
        },
        meta: {
            maxWaitForFbqMs: 5000,
            retryEveryMs: 250
        },
        ga4: {
            enabled: true,
            eventName: 'trail_high_intent_sent'
        },
        tiers: null,
        activeTier: null,
        attribution: {
            enabled: true,
            storageKey: 'trail_session_data',
            maxAgeDays: 30,
            exposeApi: true
        },
        conversion: {
            enabled: false,
            eventName: 'trail_conversion',
            pathPrefixes: [],
            oncePerSession: true,
            requireAttribution: false,
            conversionType: 'subscription',
            ga4: { enabled: true }
        }
    };

    const TRAIL_SESSION_STORAGE_KEY = 'trail_session_data';

    const defaultAttributionConfig = {
        enabled: true,
        storageKey: TRAIL_SESSION_STORAGE_KEY,
        maxAgeDays: 30,
        exposeApi: true
    };

    const defaultConversionConfig = {
        enabled: false,
        eventName: 'trail_conversion',
        pathPrefixes: [],
        oncePerSession: true,
        requireAttribution: false,
        conversionType: 'subscription',
        ga4: { enabled: true }
    };

    function getAttributionConfig(attCfg) {
        const base = Object.assign({}, defaultAttributionConfig);
        if (!attCfg || typeof attCfg !== 'object') return base;
        return Object.assign(base, attCfg);
    }

    function getConversionConfig(convCfg) {
        const base = Object.assign({}, defaultConversionConfig);
        if (!convCfg || typeof convCfg !== 'object') return base;
        const merged = Object.assign(base, convCfg);
        if (convCfg.ga4 && typeof convCfg.ga4 === 'object') {
            merged.ga4 = Object.assign({}, base.ga4, convCfg.ga4);
        }
        return merged;
    }

    function parseQueryParam(search, key) {
        if (!search) return '';
        try {
            if (typeof URLSearchParams !== 'undefined') {
                const v = new URLSearchParams(search).get(key);
                return v ? String(v) : '';
            }
        } catch (e) { /* fall through */ }
        const q = search.charAt(0) === '?' ? search.slice(1) : search;
        const parts = q.split('&');
        for (let i = 0; i < parts.length; i++) {
            const pair = parts[i].split('=');
            if (decodeURIComponent(pair[0] || '') === key) {
                return decodeURIComponent((pair[1] || '').replace(/\+/g, ' '));
            }
        }
        return '';
    }

    function parseAttributionFromUrl(search) {
        const s = search != null ? search : ((typeof window !== 'undefined' && window.location) ? window.location.search : '');
        const out = {};
        const src = parseQueryParam(s, 'utm_source');
        const camp = parseQueryParam(s, 'utm_campaign');
        const term = parseQueryParam(s, 'utm_term');
        const content = parseQueryParam(s, 'utm_content');
        let campaignId = parseQueryParam(s, 'campaign_id');
        if (!campaignId) campaignId = parseQueryParam(s, 'utm_id');
        if (src) out.source = src;
        if (camp) out.campaign = camp;
        if (term) out.term = term;
        if (content) out.content = content;
        if (campaignId) out.campaign_id = campaignId;
        return out;
    }

    function readTrailSessionRecord(storageKey) {
        const key = storageKey || TRAIL_SESSION_STORAGE_KEY;
        try {
            const raw = localStorage.getItem(key);
            const data = raw ? safeJsonParse(raw, null) : null;
            return (data && typeof data === 'object') ? data : null;
        } catch (e) {
            return null;
        }
    }

    function writeTrailSessionRecord(storageKey, data) {
        const key = storageKey || TRAIL_SESSION_STORAGE_KEY;
        try {
            localStorage.setItem(key, JSON.stringify(data));
        } catch (e) { /* ignore */ }
    }

    function readAttributionStorage(attCfg) {
        const cfg = getAttributionConfig(attCfg);
        if (cfg.enabled === false) return {};
        const session = readTrailSessionRecord(cfg.storageKey);
        const att = (session && session.attribution && typeof session.attribution === 'object')
            ? session.attribution
            : {};
        if (cfg.maxAgeDays && att.captured_at) {
            const maxMs = Number(cfg.maxAgeDays) * 24 * 60 * 60 * 1000;
            if (Number.isFinite(maxMs) && maxMs > 0 && (nowMs() - Number(att.captured_at)) > maxMs) {
                return {};
            }
        }
        return att;
    }

    function writeAttributionStorage(attCfg, attribution) {
        const cfg = getAttributionConfig(attCfg);
        const key = cfg.storageKey || TRAIL_SESSION_STORAGE_KEY;
        const now = nowMs();
        let session = readTrailSessionRecord(key);
        if (!session) {
            session = { createdAt: now, lastActivity: now };
        }
        session.attribution = attribution;
        session.lastActivity = now;
        writeTrailSessionRecord(key, session);
    }

    function captureAttribution(attCfg) {
        const cfg = getAttributionConfig(attCfg);
        if (cfg.enabled === false) return;
        const fromUrl = parseAttributionFromUrl();
        const keys = Object.keys(fromUrl);
        if (keys.length === 0) return;
        const prev = readAttributionStorage(attCfg);
        const merged = Object.assign({}, prev);
        for (let i = 0; i < keys.length; i++) {
            merged[keys[i]] = fromUrl[keys[i]];
        }
        merged.captured_at = nowMs();
        writeAttributionStorage(attCfg, merged);
    }

    function getAttribution(attCfg) {
        return readAttributionStorage(attCfg);
    }

    function buildAttributionGa4Params(attCfg) {
        const ga4Params = {};
        appendAttributionToGa4Params(ga4Params, getAttribution(attCfg));
        return ga4Params;
    }

    function isAttributionExpired(attCfg) {
        const cfg = getAttributionConfig(attCfg);
        if (cfg.enabled === false) return true;
        const session = readTrailSessionRecord(cfg.storageKey);
        const att = (session && session.attribution && typeof session.attribution === 'object')
            ? session.attribution
            : null;
        if (!att || !att.captured_at) return true;
        if (!cfg.maxAgeDays) return false;
        const maxMs = Number(cfg.maxAgeDays) * 24 * 60 * 60 * 1000;
        return Number.isFinite(maxMs) && maxMs > 0 && (nowMs() - Number(att.captured_at)) > maxMs;
    }

    function wasConversionFiredInSession(storageKey, sessionId) {
        if (!sessionId) return false;
        const session = readTrailSessionRecord(storageKey);
        return !!(session && session.conversion_last_session_id === sessionId);
    }

    function markConversionFiredInSession(storageKey, sessionId) {
        if (!sessionId) return;
        const now = nowMs();
        let session = readTrailSessionRecord(storageKey);
        if (!session) {
            session = { createdAt: now, lastActivity: now };
        }
        session.conversion_last_session_id = sessionId;
        session.lastActivity = now;
        writeTrailSessionRecord(storageKey, session);
    }

    function buildGa4ConversionParams(convCfg, sessionId, path, attribution) {
        const ga4Params = {
            event_category: 'trail_conversion',
            conversion_type: convCfg.conversionType || 'subscription',
            session_id: sessionId,
            page_path: path,
            page_location: (typeof window !== 'undefined' && window.location) ? window.location.href : ''
        };
        appendAttributionToGa4Params(ga4Params, attribution);
        return ga4Params;
    }

    function wouldFireConversion(opts) {
        opts = opts || {};
        const convCfg = getConversionConfig(opts.conversion);
        if (convCfg.enabled === false) return false;
        const prefixes = convCfg.pathPrefixes || [];
        if (!prefixes.length) return false;
        const path = (typeof window !== 'undefined' && window.location) ? window.location.pathname : '';
        if (!matchesPathPrefixes(path, prefixes)) return false;
        const storageKey = getAttributionConfig(opts.attribution).storageKey || TRAIL_SESSION_STORAGE_KEY;
        if (convCfg.oncePerSession !== false && opts.sessionId && wasConversionFiredInSession(storageKey, opts.sessionId)) {
            return false;
        }
        const attribution = getAttribution(opts.attribution);
        if (convCfg.requireAttribution && !attribution.term) return false;
        return true;
    }

    function tryFireConversionEvent(opts) {
        opts = opts || {};
        const convCfg = getConversionConfig(opts.conversion);
        const attCfg = opts.attribution;
        const sessionId = opts.sessionId;
        const debug = opts.debug;

        if (convCfg.enabled === false) return false;
        const prefixes = convCfg.pathPrefixes || [];
        if (!prefixes.length) {
            if (debug) console.log('TrailConversion: no pathPrefixes configured');
            return false;
        }

        const path = (typeof window !== 'undefined' && window.location) ? window.location.pathname : '';
        if (!matchesPathPrefixes(path, prefixes)) return false;

        const storageKey = getAttributionConfig(attCfg).storageKey || TRAIL_SESSION_STORAGE_KEY;
        if (convCfg.oncePerSession !== false && sessionId && wasConversionFiredInSession(storageKey, sessionId)) {
            if (debug) console.log('TrailConversion: already fired this session');
            return false;
        }

        const attribution = getAttribution(attCfg);
        if (convCfg.requireAttribution && !attribution.term) {
            if (debug) console.log('TrailConversion: requireAttribution but no term');
            return false;
        }

        const ga4Cfg = convCfg.ga4 || {};
        if (ga4Cfg.enabled === false) {
            if (debug) console.log('TrailConversion: ga4 disabled');
            if (convCfg.oncePerSession !== false && sessionId) markConversionFiredInSession(storageKey, sessionId);
            return true;
        }

        const eventName = convCfg.eventName || 'trail_conversion';
        const params = buildGa4ConversionParams(convCfg, sessionId, path, attribution);

        const sent = sendGa4Event(ga4Cfg, eventName, params, { config: getConfig(), debug: debug });
        if (sent) {
            if (convCfg.oncePerSession !== false && sessionId) markConversionFiredInSession(storageKey, sessionId);
            if (debug) console.log('TrailConversion: sent', { event: eventName, params: params });
        }
        return sent;
    }

    function registerTrailAttributionApi() {
        const trailCfg = (window.TrailConfig && window.TrailConfig.trailScoreMeta) || {};
        const attCfg = trailCfg.attribution;
        const convCfg = trailCfg.conversion;
        const apiCfg = getAttributionConfig(attCfg);
        if (apiCfg.exposeApi === false) return;

        window.TrailAttribution = {
            getAttribution: function() { return getAttribution(attCfg); },
            getGa4Params: function() { return buildAttributionGa4Params(attCfg); },
            captureFromUrl: function() {
                captureAttribution(attCfg);
                return getAttribution(attCfg);
            },
            isEnabled: function() { return getAttributionConfig(attCfg).enabled !== false; },
            isExpired: function() { return isAttributionExpired(attCfg); },
            wouldFireConversion: function() {
                const tracker = window.TrailTracker;
                const sid = tracker && tracker.sessionId;
                return wouldFireConversion({
                    conversion: convCfg,
                    attribution: attCfg,
                    sessionId: sid
                });
            }
        };
    }

    function appendAttributionToGa4Params(ga4Params, attribution) {
        if (!attribution || typeof attribution !== 'object') return;
        const fields = ['source', 'campaign', 'term', 'content', 'campaign_id'];
        for (let i = 0; i < fields.length; i++) {
            const k = fields[i];
            if (attribution[k]) ga4Params[k] = attribution[k];
        }
    }

    function resolveGlobalSendTo(config) {
        const ga = config && config.analytics && config.analytics.googleAnalytics;
        if (!ga) return null;
        const raw = ga.sendTo != null ? ga.sendTo : ga.measurementId;
        if (!raw) return null;
        if (Array.isArray(raw)) {
            const ids = raw.map(String).map(function(s) { return s.trim(); }).filter(Boolean);
            return ids.length ? ids : null;
        }
        const s = String(raw).trim();
        return s || null;
    }

    function sendGa4Event(ga4Cfg, eventName, params, opts) {
        opts = opts || {};
        if (!ga4Cfg || ga4Cfg.enabled === false) return false;
        if (typeof window.gtag !== 'function') {
            if (opts.debug) console.warn('Trail: gtag not available');
            return false;
        }
        const payload = Object.assign({}, params);
        const sendTo = resolveGlobalSendTo(opts.config);
        if (sendTo) payload.send_to = sendTo;
        try {
            window.gtag('event', eventName, payload);
            return true;
        } catch (e) {
            if (opts.debug) console.warn('Trail: GA4 event failed', e);
            return false;
        }
    }

    function buildGa4HighIntentParams(metaPayload, resolved, sessionId, attribution) {
        const ga4Params = {
            event_category: 'trail_meta',
            event_label: resolved.eventName,
            session_id: sessionId,
            score: metaPayload.score,
            threshold: metaPayload.threshold,
            page_path: metaPayload.page_path,
            page_location: metaPayload.page_url,
            trail_data: JSON.stringify(metaPayload)
        };
        if (resolved.activeTier) ga4Params.tier = resolved.activeTier;
        appendAttributionToGa4Params(ga4Params, attribution);
        return ga4Params;
    }

    function slugifyMetaEventName(name) {
        const s = String(name || 'trail_high_intent').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        const out = s.slice(0, 48);
        return out || 'intent';
    }

    function resolveTrailScoreTier(cfg) {
        const tiers = cfg.tiers;
        const active = cfg.activeTier;
        if (tiers && typeof tiers === 'object' && active != null && active !== '') {
            const t = tiers[active];
            if (t && typeof t === 'object') {
                const en = t.eventName;
                const th = Number(t.threshold);
                if (typeof en === 'string' && en && Number.isFinite(th)) {
                    return { eventName: en, threshold: th, activeTier: String(active) };
                }
            }
        }
        const flatName = (typeof cfg.eventName === 'string' && cfg.eventName) ? cfg.eventName : 'trail_high_intent';
        return { eventName: flatName, threshold: Number(cfg.threshold), activeTier: null };
    }

    function effectiveIntentStorage(cfg, resolved) {
        const base = Object.assign({}, defaultTrailScoreMetaConfig.storage, cfg.storage || {});
        const legacyCompat = !resolved.activeTier && resolved.eventName === 'trail_high_intent';
        if (legacyCompat) {
            return base;
        }
        const slug = slugifyMetaEventName(resolved.eventName);
        return Object.assign({}, base, {
            lastSentDayKey: `trail_intent_${slug}_last_sent_day`,
            sentInSessionPrefix: `trail_intent_${slug}_sent_`,
            lockPrefix: `trail_intent_${slug}_lock_`
        });
    }

    function safeJsonParse(s, fallback) {
        try { return JSON.parse(s); } catch (e) { return fallback; }
    }

    function nowMs() { return Date.now(); }

    function isoDayUtc() { return new Date().toISOString().slice(0, 10); }

    function isoDayLocal() {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    function todayKey(dayMode) {
        return dayMode === 'local' ? isoDayLocal() : isoDayUtc();
    }

    function getOrCreateUserId(storage) {
        const k = storage.userIdKey;
        try {
            const existing = localStorage.getItem(k);
            if (existing) return existing;
            const uid = `u_${nowMs()}_${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem(k, uid);
            return uid;
        } catch (e) {
            return `u_mem_${nowMs()}_${Math.random().toString(36).slice(2, 10)}`;
        }
    }

    function isExcludedPath(path, prefixes) {
        if (!path) return false;
        const list = prefixes || [];
        return list.some(p => path.startsWith(p));
    }

    function matchesPathPrefixes(path, prefixes) {
        if (!path) return false;
        const list = prefixes || [];
        if (!list.length) return false;
        return list.some(p => path.startsWith(p));
    }

    function loadSessionState(cfg, sessionId) {
        const key = `${cfg.storage.sessionPrefix}${sessionId}`;
        try {
            const raw = localStorage.getItem(key);
            const data = raw ? safeJsonParse(raw, null) : null;
            if (!data || typeof data !== 'object') return { key, data: { lastActivity: nowMs(), counts: {} } };
            const last = Number(data.lastActivity || 0);
            if (!last || (nowMs() - last > cfg.sessionTtlMs)) {
                return { key, data: { lastActivity: nowMs(), counts: {} } };
            }
            if (!data.counts || typeof data.counts !== 'object') data.counts = {};
            return { key, data };
        } catch (e) {
            return { key, data: { lastActivity: nowMs(), counts: {} } };
        }
    }

    function saveSessionState(storageKey, data) {
        data.lastActivity = nowMs();
        try { localStorage.setItem(storageKey, JSON.stringify(data)); } catch (e) {}
    }

    function incCount(counts, key, inc) {
        if (!key) return;
        const v = (inc === undefined) ? 1 : inc;
        const prev = Number(counts[key] || 0);
        counts[key] = prev + v;
    }

    function computeScore(weights, counts) {
        let score = 0;
        const w = weights || {};
        for (const [k, ww0] of Object.entries(w)) {
            const ww = Number(ww0);
            if (!Number.isFinite(ww) || Math.abs(ww) < 1e-9) continue;
            if (Number(counts[k] || 0) > 0) score += ww;
        }
        return score;
    }

    function wasSentToday(cfg) {
        const day = todayKey(cfg.dayMode);
        try { return localStorage.getItem(cfg.storage.lastSentDayKey) === day; } catch (e) { return false; }
    }

    function markSentToday(cfg) {
        try { localStorage.setItem(cfg.storage.lastSentDayKey, todayKey(cfg.dayMode)); } catch (e) {}
    }

    function sentInSessionKey(cfg, sessionId) {
        return `${cfg.storage.sentInSessionPrefix}${sessionId}_${todayKey(cfg.dayMode)}`;
    }

    function lockKey(cfg, sessionId) {
        return `${cfg.storage.lockPrefix}${sessionId}_${todayKey(cfg.dayMode)}`;
    }

    function wasSentInSession(cfg, sessionId) {
        const k = sentInSessionKey(cfg, sessionId);
        try { return localStorage.getItem(k) === '1'; } catch (e) { return false; }
    }

    function markSentInSession(cfg, sessionId) {
        const k = sentInSessionKey(cfg, sessionId);
        try { localStorage.setItem(k, '1'); } catch (e) {}
    }

    function isLocked(cfg, sessionId) {
        const k = lockKey(cfg, sessionId);
        try {
            const v = localStorage.getItem(k);
            if (!v) return false;
            const ts = Number(v);
            if (!ts) return true;
            return (nowMs() - ts) < 10000; // 10s ventana anti-race
        } catch (e) { return false; }
    }

    function lock(cfg, sessionId) {
        const k = lockKey(cfg, sessionId);
        try { localStorage.setItem(k, String(nowMs())); } catch (e) {}
    }

    function unlock(cfg, sessionId) {
        const k = lockKey(cfg, sessionId);
        try { localStorage.removeItem(k); } catch (e) {}
    }

    function applySignalMapping(detail, counts) {
        const type = (detail && detail.event_type) ? detail.event_type : '';
        const path = (detail && detail.page_path) ? detail.page_path : (window.location && window.location.pathname) ? window.location.pathname : '';
        const element = detail && detail.element ? detail.element : null;
        const field = detail && detail.field ? detail.field : null;
        const href = (element && element.href) ? element.href : '';
        const text = (element && element.text ? element.text : '').toString().toLowerCase();
        const fieldName = (field && (field.name || field.id) ? (field.name || field.id) : '').toString().toLowerCase();

        if (type === 'page_load' || type === 'page_navigation') {
            if (path.includes('suscripciones')) incCount(counts, 'visited_suscripciones', 1);
            if (path.includes('/products/') && (path.includes('plan') || path.includes('brasil') || path.includes('mundial'))) {
                incCount(counts, 'visited_plan_pdp', 1);
            }
        }

        if (type === 'click') {
            const h = href.toString().toLowerCase();
            const isSuscripciones = h.includes('suscripciones') || text.includes('suscripciones') || path.includes('suscripciones');
            const isCafe = h.includes('/collections/cafe') || (text.includes('colecciones') && text.includes('cafe')) || (h.includes('collections') && h.includes('cafe'));
            const isKiosco = h.includes('kiosco');
            const isMerch = h.includes('merch');

            if (isSuscripciones) incCount(counts, 'trail_click_on_suscripciones_collection_count', 1);
            if (isCafe) incCount(counts, 'trail_click_on_collections_cafe_count', 1);
            if (isKiosco) incCount(counts, 'trail_click_on_collections_kiosco_count', 1);
            if (isMerch) incCount(counts, 'trail_click_on_collections_merch_count', 1);

            if (h.includes('plan-mundial') || h.includes('plan_mundial') || text.includes('plan mundial')) {
                incCount(counts, 'trail_click_on_plan_mundial_count', 1);
            }
            if (h.includes('plan-brasil') || h.includes('plan_brasil') || text.includes('plan brasil')) {
                incCount(counts, 'trail_click_on_plan_brasil_count', 1);
            }
            if (h.includes('brasil-crucera') || h.includes('brasil_crucera') || text.includes('brasil crucera')) {
                incCount(counts, 'trail_click_on_brasil_crucera_count', 1);
            }
        }

        if (type === 'form_field_change') {
            if (fieldName.includes('molienda')) incCount(counts, 'trail_form_change_molienda_count', 1);
            if (fieldName.includes('cantidad')) incCount(counts, 'trail_form_change_cantidad_count', 1);
            if (fieldName.includes('tamano') || fieldName.includes('tamaño')) incCount(counts, 'trail_form_change_tamano_count', 1);
        }
    }

    function emitAdpulseInteraction(detail) {
        try {
            if (typeof window.CustomEvent === 'function') {
                window.dispatchEvent(new CustomEvent('adpulse-interaction', { detail }));
            } else {
                const event = document.createEvent('CustomEvent');
                event.initCustomEvent('adpulse-interaction', false, false, detail);
                window.dispatchEvent(event);
            }
        } catch (e) {
            // fail-safe: no romper tracking base
        }
    }

    class TrailScoreMeta {
        constructor(config) {
            this.config = Object.assign({}, defaultTrailScoreMetaConfig, config || {});
            captureAttribution(this.config.attribution);
            this.userId = getOrCreateUserId(this.config.storage);
            this._pendingTimers = {};
            this._resolved = resolveTrailScoreTier(this.config);
            this._gateStorage = effectiveIntentStorage(this.config, this._resolved);
        }

        handleInteraction(data) {
            const cfg = this.config;
            if (!cfg.enabled) return;
            if (!data || !data.session_id) return;

            const path = data.page_path || (window.location && window.location.pathname) || '';
            if (isExcludedPath(path, cfg.excludePathPrefixes)) return;

            const resolved = this._resolved;
            const gateCfg = Object.assign({}, cfg, {
                storage: this._gateStorage,
                threshold: resolved.threshold,
                eventName: resolved.eventName
            });

            const sessionId = data.session_id;
            const { key, data: sessionData } = loadSessionState(cfg, sessionId);
            const counts = sessionData.counts || {};

            applySignalMapping(data, counts);

            const score = computeScore(cfg.weights, counts);
            const threshold = Number(resolved.threshold);

            saveSessionState(key, Object.assign({}, sessionData, { counts }));

            if (cfg.debug && score >= threshold) {
                console.log('TrailScoreMeta: threshold reached', {
                    sessionId,
                    score,
                    threshold,
                    activeTier: resolved.activeTier,
                    meta_event: resolved.eventName,
                    counts
                });
            }

            if (!Number.isFinite(threshold)) return;
            if (score < threshold) return;
            if (wasSentToday(gateCfg)) return;
            if (wasSentInSession(gateCfg, sessionId)) return;
            if (isLocked(gateCfg, sessionId)) return;

            lock(gateCfg, sessionId);

            const metaPayload = {
                score,
                threshold,
                version: cfg.version,
                user_id: this.userId,
                session_id: sessionId,
                page_path: path,
                page_url: data.page_url || window.location.href,
                ...(resolved.activeTier ? { tier: resolved.activeTier } : {}),
                meta_event: resolved.eventName
            };

            const attribution = getAttribution(cfg.attribution);

            this._sendToMetaWhenReady(resolved.eventName, metaPayload, () => {
                markSentInSession(gateCfg, sessionId);
                markSentToday(gateCfg);
                unlock(gateCfg, sessionId);

                if (cfg.ga4 && cfg.ga4.enabled) {
                    sendGa4Event(
                        cfg.ga4,
                        cfg.ga4.eventName || 'trail_high_intent_sent',
                        buildGa4HighIntentParams(metaPayload, resolved, sessionId, attribution),
                        { config: getConfig(), debug: cfg.debug }
                    );
                }

                if (cfg.debug) {
                    console.log('TrailScoreMeta: sent', {
                        event: resolved.eventName,
                        meta: metaPayload,
                        ga4_attribution: attribution
                    });
                }
            }, () => {
                unlock(gateCfg, sessionId);
            });
        }

        _sendToMetaWhenReady(metaEventName, payload, onSuccess, onFail) {
            const cfg = this.config;
            const start = nowMs();
            const maxWait = (cfg.meta && Number(cfg.meta.maxWaitForFbqMs)) || 0;
            const retryEvery = (cfg.meta && Number(cfg.meta.retryEveryMs)) || 250;

            const attempt = () => {
                if (typeof window.fbq === 'function') {
                    try {
                        window.fbq('trackCustom', metaEventName, payload);
                        onSuccess();
                    } catch (e) {
                        if (cfg.debug) console.warn('TrailScoreMeta: fbq failed', e);
                        onFail();
                    }
                    return;
                }

                if (maxWait > 0 && (nowMs() - start) < maxWait) {
                    setTimeout(attempt, retryEvery);
                    return;
                }

                if (cfg.debug) console.warn('TrailScoreMeta: fbq not available');
                onFail();
            };

            attempt();
        }
    }

    // ============================================
    // TRAIL TRACKER CLASS
    // ============================================
    class TrailTracker {
        constructor() {
            // Leer configuración cuando se inicializa (no cuando se carga el script)
            this.config = getConfig();
            this.sessionId = this.generateSessionId();
            this.pageLoadTime = Date.now();
            this.interactions = [];
            this.trailScoreMeta = null;

            if (this.config.trailScoreMeta) {
                captureAttribution(this.config.trailScoreMeta.attribution);
                tryFireConversionEvent({
                    conversion: this.config.trailScoreMeta.conversion,
                    attribution: this.config.trailScoreMeta.attribution,
                    sessionId: this.sessionId,
                    debug: this.config.debug || this.config.trailScoreMeta.debug
                });
            }

            // Sidecar: TrailScore → Meta
            if (this.config.trailScoreMeta && this.config.trailScoreMeta.enabled) {
                try {
                    this.trailScoreMeta = new TrailScoreMeta(this.config.trailScoreMeta);
                } catch (e) {
                    if (this.config.debug) console.warn('Trail: Failed to init TrailScoreMeta', e);
                }
            }
            
            if (this.config.enabled !== false) {
                this.init();
            }
        }

        generateSessionId() {
            const STORAGE_KEY = TRAIL_SESSION_STORAGE_KEY;
            const INACTIVITY_TIMEOUT = this.config.sessionTimeout || (24 * 60 * 60 * 1000); // 24 horas por defecto
            
            try {
                // Intentar leer sessionId guardado
                const storedData = localStorage.getItem(STORAGE_KEY);
                let preservedAttribution = null;
                
                if (storedData) {
                    try {
                        const sessionData = JSON.parse(storedData);
                        if (sessionData.attribution && typeof sessionData.attribution === 'object') {
                            preservedAttribution = sessionData.attribution;
                        }
                        const now = Date.now();
                        const timeSinceLastActivity = now - (sessionData.lastActivity || sessionData.createdAt || 0);
                        
                        // Si la última actividad fue hace menos de 24 horas, reutilizar sessionId
                        if (timeSinceLastActivity < INACTIVITY_TIMEOUT && sessionData.sessionId) {
                            if (this.config.debug) {
                                console.log('Trail: Reusing existing session', {
                                    sessionId: sessionData.sessionId,
                                    lastActivity: new Date(sessionData.lastActivity).toISOString(),
                                    hoursSinceActivity: (timeSinceLastActivity / (60 * 60 * 1000)).toFixed(2)
                                });
                            }
                            return sessionData.sessionId;
                        } else {
                            // Sesión expirada por inactividad
                            if (this.config.debug) {
                                console.log('Trail: Session expired due to inactivity', {
                                    oldSessionId: sessionData.sessionId,
                                    hoursSinceActivity: (timeSinceLastActivity / (60 * 60 * 1000)).toFixed(2)
                                });
                            }
                        }
                    } catch (e) {
                        // Si hay error parseando, generar nuevo sessionId
                        if (this.config.debug) {
                            console.warn('Trail: Error parsing stored session data, creating new session');
                        }
                    }
                }
                
                // Generar nuevo sessionId
                const newSessionId = 'trail_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                const now = Date.now();
                
                // Guardar en localStorage
                const sessionData = {
                    sessionId: newSessionId,
                    createdAt: now,
                    lastActivity: now
                };
                if (preservedAttribution) {
                    sessionData.attribution = preservedAttribution;
                }
                
                localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
                
                if (this.config.debug) {
                    console.log('Trail: New session created', {
                        sessionId: newSessionId,
                        timestamp: new Date(now).toISOString()
                    });
                }
                
                return newSessionId;
                
            } catch (error) {
                // Si localStorage no está disponible (modo privado, etc.), usar sessionId temporal
                if (this.config.debug) {
                    console.warn('Trail: localStorage not available, using temporary sessionId', error);
                }
                return 'trail_temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            }
        }

        /**
         * Actualizar heartbeat de la sesión (última actividad)
         */
        updateSessionHeartbeat() {
            const STORAGE_KEY = TRAIL_SESSION_STORAGE_KEY;
            
            try {
                const storedData = localStorage.getItem(STORAGE_KEY);
                
                if (storedData) {
                    const sessionData = JSON.parse(storedData);
                    sessionData.lastActivity = Date.now();
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
                }
            } catch (error) {
                // Silenciosamente ignorar errores de localStorage
                if (this.config.debug) {
                    console.warn('Trail: Error updating session heartbeat:', error);
                }
            }
        }

        init() {
            // Esperar a que el DOM esté listo
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.initializeTrackers());
            } else {
                this.initializeTrackers();
            }
        }

        initializeTrackers() {
            if (this.config.events?.pageLoad !== false) this.trackPageLoad();
            if (this.config.events?.clicks !== false) this.trackClicks();
            if (this.config.events?.formInteractions !== false) this.trackFormInteractions();
            if (this.config.events?.formSubmissions !== false) this.trackFormSubmissions();
            if (this.config.events?.pageNavigation !== false) this.trackPageNavigation();
            if (this.config.events?.scrolling !== false) this.trackScrolling();
            if (this.config.events?.timeOnPage !== false) this.trackTimeOnPage();
            if (this.config.events?.videoInteractions !== false) this.trackVideoInteractions();
            
            if (this.config.debug) {
                console.log('Trail: Tracker initialized', {
                    sessionId: this.sessionId,
                    page: window.location.pathname,
                    timestamp: new Date().toISOString(),
                    analyticsEnabled: this.config.analytics?.googleAnalytics?.enabled || false
                });
            }
        }

        trackPageLoad() {
            const data = {
                event_type: 'page_load',
                page_url: window.location.href,
                page_path: window.location.pathname,
                page_title: document.title,
                referrer: document.referrer || 'direct',
                screen_resolution: `${window.screen.width}x${window.screen.height}`,
                viewport_size: `${window.innerWidth}x${window.innerHeight}`,
                timestamp: new Date().toISOString(),
                session_id: this.sessionId
            };
            
            this.logInteraction(data);
        }

        trackClicks() {
            document.addEventListener('click', (event) => {
                const element = event.target;
                if (!element || this.shouldIgnoreElement(element)) return;
                if (!this.isRelevantClickElement(element)) return;
                
                const tagName = element.tagName.toLowerCase();
                let elementInfo = {
                    tag: tagName,
                    classes: element.className || '',
                    id: element.id || '',
                    text: this.getElementText(element),
                    href: element.href || '',
                    type: element.type || ''
                };

                if (tagName === 'a') {
                    elementInfo.link_type = this.getLinkType(element);
                } else if (tagName === 'button' || element.type === 'button' || element.type === 'submit') {
                    elementInfo.button_type = element.type || 'button';
                    elementInfo.form_id = element.form ? element.form.id : '';
                }

                const data = {
                    event_type: 'click',
                    element: elementInfo,
                    page_url: window.location.href,
                    click_coordinates: {
                        x: event.clientX,
                        y: event.clientY
                    },
                    timestamp: new Date().toISOString(),
                    session_id: this.sessionId
                };

                this.logInteraction(data);
            });
        }

        trackFormInteractions() {
            // Focus
            document.addEventListener('focus', (event) => {
                const element = event.target;
                if (this.isFormElement(element)) {
                    this.logInteraction({
                        event_type: 'form_field_focus',
                        field: {
                            name: element.name || '',
                            id: element.id || '',
                            type: element.type || '',
                            tag: element.tagName.toLowerCase(),
                            form_id: element.form ? element.form.id : ''
                        },
                        page_url: window.location.href,
                        timestamp: new Date().toISOString(),
                        session_id: this.sessionId
                    });
                }
            }, true);

            // Change
            document.addEventListener('change', (event) => {
                const element = event.target;
                if (this.isFormElement(element)) {
                    this.logInteraction({
                        event_type: 'form_field_change',
                        field: {
                            name: element.name || '',
                            id: element.id || '',
                            type: element.type || '',
                            tag: element.tagName.toLowerCase(),
                            form_id: element.form ? element.form.id : '',
                            value_length: element.value ? element.value.length : 0,
                            has_value: !!element.value
                        },
                        page_url: window.location.href,
                        timestamp: new Date().toISOString(),
                        session_id: this.sessionId
                    });
                }
            });

            // Blur
            document.addEventListener('blur', (event) => {
                const element = event.target;
                if (this.isFormElement(element)) {
                    this.logInteraction({
                        event_type: 'form_field_blur',
                        field: {
                            name: element.name || '',
                            id: element.id || '',
                            type: element.type || '',
                            tag: element.tagName.toLowerCase(),
                            form_id: element.form ? element.form.id : '',
                            filled: !!element.value
                        },
                        page_url: window.location.href,
                        timestamp: new Date().toISOString(),
                        session_id: this.sessionId
                    });
                }
            }, true);
        }

        trackFormSubmissions() {
            document.addEventListener('submit', (event) => {
                const form = event.target;
                const formData = new FormData(form);
                const fields = {};
                
                for (let [key, value] of formData.entries()) {
                    if (this.isSensitiveField(key)) {
                        fields[key] = { has_value: !!value, length: value.length };
                    } else {
                        fields[key] = { has_value: !!value, length: value.length, value: value };
                    }
                }

                this.logInteraction({
                    event_type: 'form_submit',
                    form: {
                        id: form.id || '',
                        action: form.action || '',
                        method: form.method || 'GET',
                        name: form.name || '',
                        field_count: form.elements.length
                    },
                    fields: fields,
                    page_url: window.location.href,
                    timestamp: new Date().toISOString(),
                    session_id: this.sessionId
                });
            });
        }

        trackPageNavigation() {
            window.addEventListener('beforeunload', () => {
                const timeOnPage = Date.now() - this.pageLoadTime;
                
                this.logInteraction({
                    event_type: 'page_exit',
                    page_url: window.location.href,
                    time_on_page: timeOnPage,
                    interactions_count: this.interactions.length,
                    timestamp: new Date().toISOString(),
                    session_id: this.sessionId
                });
                
                this.flushInteractions();
            });
        }

        trackScrolling() {
            let scrollTimeout;
            let maxScrollPercentage = 0;
            const debounceMs = this.config.scroll?.debounceMs || 500;
            const milestones = this.config.scroll?.trackPercentages || [25, 50, 75, 90, 100];
            const reachedMilestones = new Set();
            
            window.addEventListener('scroll', () => {
                clearTimeout(scrollTimeout);
                
                const scrollPercentage = Math.round(
                    (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
                );
                
                if (scrollPercentage > maxScrollPercentage) {
                    maxScrollPercentage = scrollPercentage;
                }
                
                // Track milestones
                milestones.forEach(milestone => {
                    if (scrollPercentage >= milestone && !reachedMilestones.has(milestone)) {
                        reachedMilestones.add(milestone);
                        this.logInteraction({
                            event_type: 'scroll_milestone',
                            scroll_percentage: milestone,
                            page_url: window.location.href,
                            timestamp: new Date().toISOString(),
                            session_id: this.sessionId
                        });
                    }
                });
                
                scrollTimeout = setTimeout(() => {
                    this.logInteraction({
                        event_type: 'scroll',
                        scroll_percentage: scrollPercentage,
                        max_scroll_percentage: maxScrollPercentage,
                        page_url: window.location.href,
                        timestamp: new Date().toISOString(),
                        session_id: this.sessionId
                    });
                }, debounceMs);
            });
        }

        trackTimeOnPage() {
            const milestones = this.config.timeMilestones || [10, 30, 60, 120, 300];
            
            milestones.forEach(milestone => {
                setTimeout(() => {
                    this.logInteraction({
                        event_type: 'time_milestone',
                        milestone_seconds: milestone,
                        page_url: window.location.href,
                        timestamp: new Date().toISOString(),
                        session_id: this.sessionId
                    });
                }, milestone * 1000);
            });
        }

        trackVideoInteractions() {
            // HTML5 videos
            this.trackHTML5Videos();
            
            // YouTube videos
            this.trackYouTubeVideos();
            
            // Video clicks
            this.trackVideoClicks();
        }

        trackHTML5Videos() {
            const videos = document.querySelectorAll('video');
            
            videos.forEach(video => {
                const videoId = video.id || video.src || `video_${Date.now()}`;
                const reachedMilestones = new Set();
                
                video.addEventListener('play', () => {
                    this.logVideoInteraction('video_play', {
                        video_id: videoId,
                        video_src: video.src || video.currentSrc,
                        current_time: video.currentTime,
                        duration: video.duration
                    });
                });
                
                video.addEventListener('pause', () => {
                    this.logVideoInteraction('video_pause', {
                        video_id: videoId,
                        video_src: video.src || video.currentSrc,
                        current_time: video.currentTime,
                        duration: video.duration
                    });
                });
                
                video.addEventListener('ended', () => {
                    this.logVideoInteraction('video_ended', {
                        video_id: videoId,
                        video_src: video.src || video.currentSrc,
                        duration: video.duration
                    });
                });
                
                video.addEventListener('timeupdate', () => {
                    if (video.duration) {
                        const percentage = Math.round((video.currentTime / video.duration) * 100);
                        [25, 50, 75, 100].forEach(milestone => {
                            if (percentage >= milestone && !reachedMilestones.has(milestone)) {
                                reachedMilestones.add(milestone);
                                this.logVideoInteraction('video_progress', {
                                    video_id: videoId,
                                    video_src: video.src || video.currentSrc,
                                    progress_percentage: milestone,
                                    current_time: video.currentTime,
                                    duration: video.duration
                                });
                            }
                        });
                    }
                });
            });
        }

        trackYouTubeVideos() {
            // Listen for YouTube iframe API messages
            window.addEventListener('message', (event) => {
                if (event.origin !== 'https://www.youtube.com') return;
                
                try {
                    const data = JSON.parse(event.data);
                    if (data.event === 'video-progress') {
                        this.logVideoInteraction('youtube_progress', {
                            video_id: this.extractYouTubeVideoId(event.source.location.href),
                            current_time: data.info?.currentTime,
                            duration: data.info?.duration,
                            progress_percentage: data.info?.currentTime && data.info?.duration ? 
                                Math.round((data.info.currentTime / data.info.duration) * 100) : 0
                        });
                    }
                } catch (e) {
                    // Ignore parsing errors
                }
            });

            // Enable YouTube iframe API for existing iframes
            setTimeout(() => {
                const youtubeIframes = document.querySelectorAll('iframe[src*="youtube.com"], iframe[src*="youtu.be"]');
                youtubeIframes.forEach(iframe => {
                    const src = iframe.src;
                    if (!src.includes('enablejsapi=1')) {
                        const separator = src.includes('?') ? '&' : '?';
                        iframe.src = src + separator + 'enablejsapi=1&origin=' + window.location.origin;
                    }
                });
            }, 2000);
        }

        trackVideoClicks() {
            document.addEventListener('click', (event) => {
                const element = event.target;
                const videoElement = element.closest('video, iframe[src*="youtube"], iframe[src*="vimeo"], .video-container');
                
                if (videoElement) {
                    const tagName = videoElement.tagName.toLowerCase();
                    let videoData = {};
                    
                    if (tagName === 'iframe') {
                        const src = videoElement.src;
                        if (src.includes('youtube.com') || src.includes('youtu.be')) {
                            videoData = {
                                video_type: 'youtube',
                                video_id: this.extractYouTubeVideoId(src),
                                video_url: src
                            };
                        } else if (src.includes('vimeo.com')) {
                            videoData = {
                                video_type: 'vimeo',
                                video_id: this.extractVimeoVideoId(src),
                                video_url: src
                            };
                        }
                    } else if (tagName === 'video') {
                        videoData = {
                            video_type: 'html5',
                            video_src: videoElement.src || videoElement.currentSrc,
                            current_time: videoElement.currentTime,
                            duration: videoElement.duration,
                            paused: videoElement.paused
                        };
                    }
                    
                    this.logVideoInteraction('video_click', {
                        ...videoData,
                        click_coordinates: {
                            x: event.clientX,
                            y: event.clientY
                        }
                    });
                }
            });
        }

        extractYouTubeVideoId(url) {
            const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
            return match ? match[1] : null;
        }

        extractVimeoVideoId(url) {
            const match = url.match(/vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|)(\d+)(?:$|\/|\?)/);
            return match ? match[3] : null;
        }

        logVideoInteraction(eventType, videoData) {
            const data = {
                event_type: eventType,
                video: videoData,
                page_url: window.location.href,
                timestamp: new Date().toISOString(),
                session_id: this.sessionId
            };
            
            this.logInteraction(data);
        }

        // ============================================
        // HELPERS
        // ============================================
        shouldIgnoreElement(element) {
            if (!element) return true;
            for (const selector of this.config.ignoreSelectors || []) {
                if (element.closest && element.closest(selector)) return true;
            }
            return false;
        }

        isRelevantClickElement(element) {
            const tagName = element.tagName.toLowerCase();
            const alwaysTrack = ['a', 'button', 'input', 'select', 'textarea', 'option', 'label', 'img'];
            if (alwaysTrack.includes(tagName)) return true;
            
            const hasClickHandler = element.onclick || element.getAttribute('onclick') || 
                                   element.hasAttribute('data-click') || element.hasAttribute('data-action');
            if (hasClickHandler) return true;
            
            const interactiveRoles = ['button', 'link', 'menuitem', 'tab', 'option'];
            const role = element.getAttribute('role');
            if (role && interactiveRoles.includes(role)) return true;
            
            const style = window.getComputedStyle(element);
            if (style.cursor === 'pointer') return true;
            
            const interactiveClasses = ['btn', 'button', 'link', 'clickable', 'interactive', 'card-hover'];
            const classes = element.className || '';
            if (interactiveClasses.some(cls => classes.includes(cls))) return true;
            
            const skipTags = ['div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'section', 'article', 'main', 'aside', 'header', 'footer', 'nav'];
            if (skipTags.includes(tagName)) return false;
            
            return true;
        }

        getElementText(element) {
            if (element.alt) return element.alt;
            if (element.title) return element.title;
            if (element.textContent) return element.textContent.trim().substring(0, 100);
            if (element.value) return '[input value]';
            return '';
        }

        getLinkType(element) {
            const href = element.href || '';
            if (href.includes('mailto:')) return 'email';
            if (href.includes('tel:')) return 'phone';
            if (href.startsWith('#')) return 'anchor';
            if (href.includes(window.location.hostname)) return 'internal';
            if (href.startsWith('http')) return 'external';
            return 'relative';
        }

        isFormElement(element) {
            const formTags = ['input', 'textarea', 'select', 'option'];
            return formTags.includes(element.tagName.toLowerCase());
        }

        isSensitiveField(fieldName) {
            const sensitiveFields = this.config.sensitiveFields || [];
            return sensitiveFields.some(field => 
                fieldName.toLowerCase().includes(field)
            );
        }

        // ============================================
        // LOGGING & ANALYTICS
        // ============================================
        logInteraction(data) {
            try {
                // Actualizar heartbeat de la sesión (última actividad)
                this.updateSessionHeartbeat();
                
                // Memory management
                const maxInteractions = this.config.dataRetention?.maxInteractionsInMemory || 1000;
                if (this.interactions.length >= maxInteractions) {
                    this.interactions.shift();
                }
                
                this.interactions.push(data);
                
                // Debug logging con prefijo "Trail:"
                if (this.config.debug) {
                    const eventName = data.event_type.toUpperCase().replace(/_/g, ' ');
                    console.log(`Trail: ${eventName}`, data);
                }

                // Compatibilidad: bus de eventos normalizado (mismo que landing)
                emitAdpulseInteraction(data);

                // Sidecar: TrailScore → Meta (usa el mismo flujo de eventos)
                if (this.trailScoreMeta) {
                    this.trailScoreMeta.handleInteraction(data);
                }
                
                // Send to analytics (solo si está habilitado)
                this.sendToAnalytics(data);
            } catch (error) {
                if (this.config.debug) {
                    console.warn('Trail: Error logging interaction:', error);
                }
            }
        }

        sendToAnalytics(data) {
            // Google Analytics 4 - Solo si está habilitado
            if (this.config.analytics?.googleAnalytics?.enabled) {
                sendGa4Event(
                    this.config.analytics.googleAnalytics,
                    'trail_' + data.event_type,
                    {
                        event_category: 'user_interaction',
                        event_label: data.event_type,
                        page_location: data.page_url,
                        page_path: data.page_path || window.location.pathname,
                        session_id: data.session_id,
                        timestamp: data.timestamp,

                        ...(data.element && {
                            element_tag: data.element.tag,
                            element_id: data.element.id,
                            element_classes: data.element.classes,
                            element_text: data.element.text?.substring(0, 100),
                            element_href: data.element.href,
                            element_type: data.element.type
                        }),

                        ...(data.form && {
                            form_id: data.form.id,
                            form_action: data.form.action,
                            form_method: data.form.method,
                            form_field_count: data.form.field_count
                        }),

                        ...(data.field && {
                            field_name: data.field.name,
                            field_id: data.field.id,
                            field_type: data.field.type,
                            field_filled: data.field.filled || data.field.has_value
                        }),

                        ...(data.video && {
                            video_id: data.video.video_id,
                            video_type: data.video.video_type,
                            video_progress: data.video.progress_percentage
                        }),

                        ...(data.scroll_percentage !== undefined && {
                            scroll_percentage: data.scroll_percentage,
                            max_scroll_percentage: data.max_scroll_percentage
                        }),

                        ...(data.milestone_seconds !== undefined && {
                            milestone_seconds: data.milestone_seconds
                        }),

                        trail_data: JSON.stringify(data)
                    },
                    { config: this.config, debug: this.config.debug }
                );
            }
            
            // Custom endpoint - Solo si está habilitado
            if (this.config.analytics?.customEndpoint?.enabled && this.config.analytics.customEndpoint.url) {
                fetch(this.config.analytics.customEndpoint.url, {
                    method: this.config.analytics.customEndpoint.method || 'POST',
                    headers: this.config.analytics.customEndpoint.headers || { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data),
                    keepalive: true // Para eventos antes de page unload
                }).catch(error => {
                    if (this.config.debug) {
                        console.warn('Trail: Failed to send to custom endpoint:', error);
                    }
                });
            }
        }

        flushInteractions() {
            if (this.config.debug) {
                console.log('Trail: Session Summary', {
                    sessionId: this.sessionId,
                    totalInteractions: this.interactions.length,
                    timeOnPage: Date.now() - this.pageLoadTime,
                    pageUrl: window.location.href
                });
            }
        }

        getSessionStats() {
            return {
                sessionId: this.sessionId,
                pageLoadTime: this.pageLoadTime,
                totalInteractions: this.interactions.length,
                timeOnPage: Date.now() - this.pageLoadTime,
                interactions: this.interactions
            };
        }
    }

    // ============================================
    // INITIALIZATION
    // ============================================
    registerTrailAttributionApi();

    // Inicializar automáticamente
    if (getConfig().enabled !== false) {
        window.TrailTracker = new TrailTracker();
    }

    // Exponer para uso externo si es necesario
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = TrailTracker;
    }

})(window, document);

